import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import {
  GalaxyColumnDef,
  GalaxyDataSource,
  GalaxyTableModule,
  PagedListRequestInterface,
  PagedResponseInterface,
} from '@vendasta/galaxy/table';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { SourceFilter, TestManagementService } from '../test-management.service';
import { AssistantKey, ListTestRunsResponse, TestRun, TriggerSource } from '@vendasta/protos/ai_assistants/v1alpha1';
import { debounce, distinctUntilChanged, map } from 'rxjs/operators';
import { merge, Observable, of, Subject, timer } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { getRunName, StatusMap } from '../test-run.utils';
import { TranslateService } from '@ngx-translate/core';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';

@Component({

  selector: 'ai-test-run-table',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatListModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    GalaxyBadgeModule,
    GalaxyFormFieldModule,
    GalaxyTableModule,
    MatTableModule,
    MatSortModule,
    AiAssistantI18nModule,
  ],
  templateUrl: './test-run-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './test-run-table.component.scss',
})
export class TestRunTableComponent implements OnInit {
  private readonly testService = inject(TestManagementService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  @Input() assistantKey: AssistantKey;
  @Input() testSuiteId: string;
  @Input() enableComparison = true;
  @Input() hasVoice = false;
  @Input() showSuccessEvalFilters = true;
  onlySuccessEvaluations = false;
  sourceFilterType: SourceFilter['type'] = 'conversationId';
  sourceIdControl = new FormControl('', { nonNullable: true });
  private refresh$ = new Subject<void>();

  @Output() compareRuns = new EventEmitter<[TestRun, TestRun]>();

  protected statusMap = StatusMap;

  // Selection for comparison
  selectedRuns = signal<Set<string>>(new Set());

  dataSource: GalaxyDataSource<TestRun, void, MatSort> = {} as GalaxyDataSource<TestRun, void, MatSort>;
  columns: GalaxyColumnDef[] = [];
  private allRuns: TestRun[] = [];

  private buildColumns(): void {
    const cols: GalaxyColumnDef[] = [];

    if (this.enableComparison) {
      cols.push({
        id: 'select',
        title: '',
      });
    }

    cols.push(
      {
        id: 'name',
        title: this.translateService.instant('AI_ASSISTANT.TESTING.RUN_TABLE.NAME'),
      },
      {
        id: 'created',
        title: this.translateService.instant('AI_ASSISTANT.TESTING.RUN_TABLE.DATE'),
        sortable: true,
      },
      {
        id: 'trigger',
        title: this.translateService.instant('AI_ASSISTANT.TESTING.RUN_TABLE.TRIGGER'),
      },
      {
        id: 'status',
        title: this.translateService.instant('AI_ASSISTANT.TESTING.RUN_TABLE.STATUS'),
      },
      {
        id: 'passed',
        title: this.translateService.instant('AI_ASSISTANT.TESTING.RUN_TABLE.PASS_RATE'),
      },
      {
        id: 'iterations',
        title: this.translateService.instant('AI_ASSISTANT.TESTING.RUN_TABLE.ITERATIONS'),
      },
    );

    this.columns = cols;
  }

  refresh(): void {
    this.dataSource.clearSelection();
    this.selectedRuns.set(new Set());
    this.dataSource.state = {
      ...this.dataSource.state,
      shouldRefetch: true,
      loading: true,
    };
  }

  toggleOnlySuccessEvaluations(value: boolean): void {
    this.onlySuccessEvaluations = value;
    this.refresh$.next();
  }

  onSourceTypeChange(value: SourceFilter['type']): void {
    this.sourceFilterType = value;
    if (this.sourceIdControl.value) {
      this.sourceIdControl.setValue('');
    }
  }

  clearSourceFilter(): void {
    this.sourceIdControl.setValue('');
  }

  sortData(sort: Sort): void {
    this.dataSource?.setSorting([
      {
        active: sort.active,
        direction: sort.direction,
      } as MatSort,
    ]);
  }

  getPassedCount(testRun: TestRun): string {
    if (testRun.resultCount > 0) {
      const passed = testRun.successCount ?? 0;
      const percentage = Math.round((passed / testRun.resultCount) * 100);
      return `${percentage}% (${passed}/${testRun.resultCount})`;
    }
    if (testRun.isSuccessEvaluation && testRun.status !== 'in_progress') {
      return '100%';
    }
    return this.translateService.instant('AI_ASSISTANT.TESTING.RUN_TABLE.NA');
  }

  getIterations(testRun: TestRun): string {
    return testRun.iterations > 0 ? `${testRun.iterations}` : '1';
  }

  // Legacy runs carry TRIGGER_SOURCE_UNSPECIFIED and render as Manual — only explicit scheduling counts.
  isScheduledRun(testRun: TestRun): boolean {
    return testRun.triggerSource === TriggerSource.TRIGGER_SOURCE_SCHEDULED;
  }

  getRunName = getRunName;

  ngOnInit(): void {
    this.buildColumns();
    this.dataSource = new GalaxyDataSource<TestRun, void, MatSort>(this);
    this.dataSource.setSorting([
      {
        active: 'created',
        direction: 'desc',
      } as MatSort,
    ]);

    const idChanges$ = this.sourceIdControl.valueChanges.pipe(
      map((v) => v.trim()),
      distinctUntilChanged(),
      debounce((v) => (v ? timer(300) : timer(0))),
      map(() => void 0),
    );

    merge(idChanges$, this.refresh$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dataSource?.setFilters([]));
  }

  get(r: PagedListRequestInterface<void, MatSort>): Observable<PagedResponseInterface<TestRun>> {
    // Determine which filter to use
    if (!this.assistantKey && !this.testSuiteId) {
      return of({ data: [], pagingMetadata: { hasMore: false, nextCursor: '' } });
    }

    const filter = this.assistantKey ? { assistantKey: this.assistantKey } : { testSuiteId: this.testSuiteId };
    const trimmedId = this.sourceIdControl.value.trim();
    const source: SourceFilter | undefined = trimmedId
      ? { type: this.sourceFilterType, value: trimmedId }
      : undefined;

    return this.testService.listTestRuns(filter, r.pagingOptions, this.onlySuccessEvaluations, source).pipe(
      map((resp: ListTestRunsResponse) => {
        const data = resp?.testRuns || [];
        this.allRuns = data;

        if (r.sorting?.length > 0) {
          const sort = r.sorting[0] as MatSort;
          data.sort((a, b) => {
            return sort.direction === 'asc'
              ? a.created.getTime() - b.created.getTime()
              : b.created.getTime() - a.created.getTime();
          });
        }
        return {
          data: data,
          pagingMetadata: resp.pagingMetadata,
        };
      }),
    );
  }

  // Selection methods for comparison
  isSelected(testRun: TestRun): boolean {
    return this.selectedRuns().has(testRun.id);
  }

  toggleSelection(testRun: TestRun, event: Event): void {
    event.stopPropagation();
    const selected = this.selectedRuns();
    const newSelected = new Set(selected);

    if (newSelected.has(testRun.id)) {
      newSelected.delete(testRun.id);
    } else {
      // Limit to 2 selections
      if (newSelected.size >= 2) {
        // Remove the oldest selection
        const first = newSelected.values().next().value;
        newSelected.delete(first);
      }
      newSelected.add(testRun.id);
    }

    this.selectedRuns.set(newSelected);
  }

  get canCompare(): boolean {
    return this.selectedRuns().size === 2;
  }

  onCompareClick(): void {
    const selectedIds = Array.from(this.selectedRuns());
    if (selectedIds.length !== 2) return;

    const runs = this.allRuns.filter((r) => selectedIds.includes(r.id));
    if (runs.length === 2) {
      this.compareRuns.emit([runs[0], runs[1]]);
      if (runs[1].created < runs[0].created) {
        // Ensure the older run is first in the URL
        selectedIds.reverse();
      }
      // Navigate to comparison page
      this.router.navigate([`compare/${selectedIds[0]}/${selectedIds[1]}`], { relativeTo: this.route });
    }
  }

  navigateToTestRun(testRun: TestRun): void {
    this.router.navigate([`./run/${testRun.id}`], { relativeTo: this.route });
  }
}
